import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Alert, Badge, Image, Table } from 'react-bootstrap'
import moment from 'moment-timezone'
import { upperCaseFirst } from 'upper-case-first'
import Confirm from '@severed-links/common.confirm'
import { adminUnblockUser, getManagedBlockedUsers } from '@severed-links/common.redherrings-reducers/reporting'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import { playerUrl } from '@severed-links/common.redherrings-constants'
import * as s from './Moderation.scss'

const PlayerDetail = ({ player = {}, playerId }) => (
    <div className={s.player}>
        <Image src={playerUrl(player.avatarUrl)} roundedCircle />
        <div>
            <strong>{player.firstName || player.lastName ? `${player.firstName || ''} ${player.lastName || ''}`.trim() : 'Unknown user'}</strong>
            <small>{player.userName || playerId}</small>
        </div>
    </div>
)

const itemTypeLabel = itemType => itemType === 'RecapComment' ? 'Recap Comment' : (itemType || 'Unknown item')

const ContentDetail = ({ item = {} }) => {
    const report = item.triggeringReportedItem || {}
    const metadata = report.metadataSnapshot || {}
    return (
        <div className={s.contentDetail}>
            <div>
                <Badge bg='secondary'>{item.app}</Badge>
                <Badge bg='light' text='dark'>{report.itemType ? itemTypeLabel(report.itemType) : item.scope}</Badge>
            </div>
            {metadata.recapTitle || metadata.recapYear ?
            <strong>{metadata.recapTitle || 'Cinderella Recap'}{metadata.recapYear ? ` (${metadata.recapYear})` : ''}</strong>
            : <strong>{item.scope}</strong>}
            {report.itemId ? <small>Item ID: {report.itemId}</small> : null}
        </div>
    )
}

const BlockedUsers = () => {
    const dispatch = useDispatch()
    const { appName = '' } = useParams()
    const app = upperCaseFirst(appName)
    const { blockedUsers = [], isFetchingBlockedUsers, isSavingBlock } = useSelector(state => state.reporting)

    useEffect(() => { dispatch(getManagedBlockedUsers(app)) }, [app])

    const doUnblock = blockedUserId => dispatch(adminUnblockUser(blockedUserId)).then(action => {
        if (action.error || action.payload?.error || action.payload?.response?.error) {
            dispatch(createNotification({
                headline: 'Unblock User',
                message: action.payload?.response?.message || action.payload?.message || 'The user could not be unblocked.',
                type: 'error',
                timeout: 6000,
            }))
        }
    })

    return (
        <div className={`${s.container} ${s[appName.toLowerCase()] || ''}`}>
            <div className={s.heading}>
                <div>
                    <h2 className={s.title}>Blocked Users</h2>
                    <p className={s.subtitle}>Active user-level content blocks for {app}.</p>
                </div>
                <Badge bg='secondary'>{blockedUsers.length}</Badge>
            </div>
            {!isFetchingBlockedUsers && !blockedUsers.length ?
            <Alert variant='light'>There are no active blocked users for {app}.</Alert>
            : null}
            {blockedUsers.length ?
            <div className='table-responsive'>
                <Table hover className={s.table}>
                    <thead>
                        <tr>
                            <th>Blocking user</th>
                            <th>Blocked user</th>
                            <th>App and content</th>
                            <th>Blocked</th>
                            <th aria-label='Actions' />
                        </tr>
                    </thead>
                    <tbody>
                        {blockedUsers.map(item => (
                            <tr key={item._id}>
                                <td><PlayerDetail player={item.blockingPlayer} playerId={item.blockingPlayerId} /></td>
                                <td><PlayerDetail player={item.blockedPlayer} playerId={item.blockedPlayerId} /></td>
                                <td><ContentDetail item={item} /></td>
                                <td>{moment(item.blockedAt).fromNow()}</td>
                                <td className='text-end'>
                                    <Confirm variant='warning'
                                        disabled={isSavingBlock}
                                        onConfirm={() => doUnblock(item._id)}
                                        heading='Remove user block?'
                                        body={`This will restore ${item.blockedPlayer?.firstName || 'the blocked user'}'s content for ${item.blockingPlayer?.firstName || 'the blocking user'}.`}
                                        buttonIcon='unlock'
                                        buttonText='Unblock'
                                        confirmText='unblock user' />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            : null}
        </div>
    )
}

export default BlockedUsers
